import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { readingPlanService } from '../../services/supabaseService';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import PlanCategorySection from './components/PlanCategorySection';
import ActivePlanCard from './components/ActivePlanCard';
import PlanConfigModal from './components/PlanConfigModal';
import ReadingInterface from './components/ReadingInterface';
import ProgressTracker from './components/ProgressTracker';
import PlanFilters from './components/PlanFilters';
import EGWPlanCard from './components/EGWPlanCard';
import EGWReadingInterface from './components/EGWReadingInterface';
import { egwPlanService, egwEnrollmentService } from '../../services/egwPlanService';
import { checkAndGrantAwards } from '../../services/awardService';
import { useAwardToast } from '../../contexts/AwardToastContext';
import { trackReadingPlanEnrolled } from '../../utils/ga';
import { BibleReadingPlansSkeleton, FadeSlideIn } from '../../components/ui/SkeletonLoader';

const BibleReadingPlans = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [activeView, setActiveView] = useState('plans'); // 'plans', 'reading', 'progress'
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [enrolledPlans, setEnrolledPlans] = useState([]);
  const [completedPlans, setCompletedPlans] = useState([]);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    duration: 'all',
    type: 'all'
  });
  const [mainTab, setMainTab] = useState('bible'); // 'bible' | 'egw'
  const [egwPlans, setEgwPlans] = useState([]);
  const [egwEnrollments, setEgwEnrollments] = useState([]);
  const [egwLoading, setEgwLoading] = useState(false);
  const [egwEnrolling, setEgwEnrolling] = useState(false);
  const [activeEgwEnrollment, setActiveEgwEnrollment] = useState(null);
  const [activeEgwPlan, setActiveEgwPlan] = useState(null);
  const [egwView, setEgwView] = useState('plans'); // 'plans' | 'reading'
  const { showAwardToasts } = useAwardToast();

  // Fetch all data on mount
  useEffect(() => {
    if (user?.id) {
      fetchAllData();
    }
  }, [user?.id]);

  // Check URL for specific view
  useEffect(() => {
    if (location?.state?.view) {
      setActiveView(location?.state?.view);
    }
  }, [location]);

  useEffect(() => {
    if (user?.id && mainTab === 'egw') {
      fetchEgwData();
    }
  }, [user?.id, mainTab]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // Fetch available plans
      const { data: plans, error: plansError } = await readingPlanService?.getReadingPlans();
      if (plansError) throw plansError;
      setAvailablePlans(plans || []);

      // Fetch user's enrolled plans
      const { data: enrolled, error: enrolledError } = await readingPlanService?.getUserReadingPlans(user?.id);
      if (enrolledError) throw enrolledError;
      
      // Separate active and completed plans
      const active = enrolled?.filter(p => p?.status === 'in_progress') || [];
      const completed = enrolled?.filter(p => p?.status === 'completed') || [];
      
      setEnrolledPlans(active);
      setCompletedPlans(completed);
    } catch (error) {
      console.error('Error fetching reading plans data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEgwData = async () => {
    setEgwLoading(true);
    try {
      const [plansResult, enrollmentsResult] = await Promise.all([
        egwPlanService?.getPublishedPlans(),
        egwEnrollmentService?.getUserEnrollments(user?.id),
      ]);
      setEgwPlans(plansResult?.data || []);
      setEgwEnrollments(enrollmentsResult?.data || []);
    } catch (err) {
      console.error('Error fetching EGW data:', err);
    } finally {
      setEgwLoading(false);
    }
  };

  const handlePlanEnroll = async (plan, config) => {
    if (!user?.id) {
      navigate('/login');
      return;
    }

    setEnrolling(true);
    try {
      const { data, error } = await readingPlanService?.enrollInPlan(user?.id, plan?.id, config);
      
      if (error) throw error;

      trackReadingPlanEnrolled(plan?.name || plan?.title || '');

      // Refresh enrolled plans
      await fetchAllData();
      
      setIsConfigModalOpen(false);
      setSelectedPlan(null);
    } catch (error) {
      console.error('Error enrolling in plan:', error);
      alert('Failed to enroll in reading plan. Please try again.');
    } finally {
      setEnrolling(false);
    }
  };

  const handleEgwEnroll = async (plan, pace) => {
    if (!user?.id) { navigate('/login'); return; }
    setEgwEnrolling(true);
    try {
      const { error } = await egwEnrollmentService?.enrollUser(user?.id, plan?.id, pace);
      if (!error) await fetchEgwData();
    } catch (err) {
      console.error('Error enrolling in EGW plan:', err);
    } finally {
      setEgwEnrolling(false);
    }
  };

  const handlePlanSelect = (plan) => {
    if (!user?.id) {
      navigate('/login');
      return;
    }
    
    // Check if user is already enrolled
    const alreadyEnrolled = enrolledPlans?.some(ep => ep?.reading_plan_id === plan?.id);
    if (alreadyEnrolled) {
      alert('You are already enrolled in this plan!');
      return;
    }

    setSelectedPlan(plan);
    setIsConfigModalOpen(true);
  };

  const handleStartReading = (plan) => {
    setSelectedPlan(plan);
    setActiveView('reading');
  };

  const handleViewProgress = (plan) => {
    setSelectedPlan(plan);
    setActiveView('progress');
  };

  const handleMarkReadingComplete = async (planId, passages) => {
    // Refresh data to update progress
    await fetchAllData();

    // Check if the plan is now fully completed and fire award
    if (user?.id && planId) {
      try {
        const { data: planData } = await readingPlanService?.getUserReadingPlans(user?.id);
        const thisPlan = planData?.find(p => p?.id === planId);

        // A plan is complete when progress reaches 100% OR all references are done
        // (status column is not auto-updated, so we check progress percentage)
        const isPlanComplete =
          thisPlan?.progress >= 100 ||
          (thisPlan?.completedReferences > 0 &&
            thisPlan?.totalReferences > 0 &&
            thisPlan?.completedReferences >= thisPlan?.totalReferences);

        if (isPlanComplete) {
          // Mark the plan as completed in the database if not already
          if (thisPlan?.status !== 'completed') {
            await supabase
              ?.from('user_reading_plans')
              ?.update({ status: 'completed', updated_at: new Date()?.toISOString() })
              ?.eq('id', planId)
              ?.eq('user_id', user?.id);
          }

          // Count total completed plans (including this one) for threshold check
          const completedCount =
            (planData?.filter(
              p =>
                p?.status === 'completed' ||
                p?.progress >= 100 ||
                (p?.completedReferences > 0 &&
                  p?.totalReferences > 0 &&
                  p?.completedReferences >= p?.totalReferences)
            )?.length) || 1;

          const awards = await checkAndGrantAwards(user?.id, 'READING_PLAN_COMPLETED', {
            totalPlans: completedCount
          });
          if (awards?.length) showAwardToasts(awards);
        }
      } catch (awardErr) {
        console.warn('Reading plan award check failed:', awardErr);
      }
    }
  };

  const handleDayChange = async (newDay) => {
    // Refresh enrolled plans to reflect updated current_day
    await fetchAllData();
  };

  const handleAddVerseToLibrary = (verse) => {
    // Handled directly in ReadingInterface via AddToLibraryModal
    // This is kept as a no-op fallback
    console.log('Adding verse to library:', verse);
  };

  const handleFilterChange = (filterType, value) => {
    setSelectedFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const filterPlans = (plans) => {
    if (!plans) return [];

    return plans?.filter(plan => {
      const matchesSearch = !searchQuery ||
        plan?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        plan?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase());

      const matchesDuration = selectedFilters?.duration === 'all' ||
        (selectedFilters?.duration === 'short' && plan?.duration_days <= 30) ||
        (selectedFilters?.duration === 'medium' && plan?.duration_days > 30 && plan?.duration_days <= 180) ||
        (selectedFilters?.duration === 'long' && plan?.duration_days > 180);
      
      const matchesType = selectedFilters?.type === 'all' || 
        plan?.category?.toLowerCase() === selectedFilters?.type?.toLowerCase();
      
      return matchesSearch && matchesDuration && matchesType;
    });
  };

  // Group plans by category
  const groupPlansByCategory = (plans) => {
    const grouped = {};
    plans?.forEach(plan => {
      const category = plan?.category || 'Other';
      if (!grouped?.[category]) {
        grouped[category] = [];
      }
      grouped?.[category]?.push(plan);
    });
    return grouped;
  };

  const getCategoryDescription = (category) => {
    const descriptions = {
      'Chronological': 'Read the Bible in the order events occurred',
      'Thematic': 'Explore specific biblical themes and topics',
      'Devotional': 'Daily readings with reflections and prayers',
      'Testament': 'Focus on Old or New Testament readings',
      'Books': 'Study individual books of the Bible',
      'Character': 'Follow biblical characters through Scripture',
      'Other': 'Various reading approaches'
    };
    return descriptions?.[category] || 'Explore biblical content';
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Chronological': 'Clock',
      'Thematic': 'Layers',
      'Devotional': 'Heart',
      'Testament': 'Book',
      'Books': 'BookOpen',
      'Character': 'Users',
      'Other': 'MoreHorizontal'
    };
    return icons?.[category] || 'BookOpen';
  };

  const todaysReading = {
    date: new Date()?.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    }),
    plan: selectedPlan?.reading_plans?.name || 'Reading Plan',
    passages: selectedPlan?.nextReading?.references?.map(ref => ({
      reference: ref,
      estimatedTime: '10 minutes'
    })) || [],
    totalTime: '20 minutes',
    completed: false,
    dayNumber: selectedPlan?.current_day || 1,
    totalDays: selectedPlan?.reading_plans?.duration_days || 365,
    catchUpNeeded: false
  };

  const handleEgwContinue = (enrollment, plan) => {
    setActiveEgwEnrollment(enrollment);
    setActiveEgwPlan(plan);
    setEgwView('reading');
  };

  const handleEgwDayComplete = async () => {
    await fetchEgwData();
  };

  const renderMainView = () => {
    switch (activeView) {
      case 'reading':
        return (
          <ReadingInterface
            reading={todaysReading}
            plan={selectedPlan}
            onComplete={(passages) => handleMarkReadingComplete(selectedPlan?.id, passages)}
            onAddVerse={handleAddVerseToLibrary}
            onBack={() => setActiveView('plans')}
            onDayChange={handleDayChange}
          />
        );
      
      case 'progress':
        return (
          <ProgressTracker
            plan={selectedPlan}
            onBack={() => setActiveView('plans')}
          />
        );
      
      default:
        const filteredPlans = filterPlans(availablePlans);
        const groupedPlans = groupPlansByCategory(filteredPlans);

        return (
          <div className="space-y-6">
            {/* Search and Filters */}
            <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
              <div className="flex-1 max-w-md">
                <Input
                  type="search"
                  placeholder="Search reading plans..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="w-full"
                />
              </div>
              <PlanFilters
                filters={selectedFilters}
                onChange={handleFilterChange}
              />
            </div>

            {loading ? (
              <BibleReadingPlansSkeleton />
            ) : (
              <>
                {/* Active Plans Section */}
                {enrolledPlans?.length > 0 && (
                  <section>
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-xl font-heading font-semibold text-foreground">
                        My Active Plans
                      </h2>
                      <span className="text-sm text-muted-foreground">
                        {enrolledPlans?.length} plan{enrolledPlans?.length !== 1 ? 's' : ''} active
                      </span>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                      {enrolledPlans?.map((plan) => (
                        <ActivePlanCard
                          key={plan?.id}
                          plan={plan}
                          onStartReading={handleStartReading}
                          onViewProgress={handleViewProgress}
                        />
                      ))}
                    </div>
                  </section>
                )}

                {/* Available Plans by Category */}
                <PlanCategorySection
                  title="Featured Plans"
                  description="Curated reading plans for spiritual growth"
                  plans={filteredPlans?.filter(p => p?.is_featured)}
                  onPlanSelect={handlePlanSelect}
                  icon="Star"
                  enrolledPlans={enrolledPlans}
                />

                {Object?.entries(groupedPlans)?.map(([category, categoryPlans]) => (
                  <PlanCategorySection
                    key={category}
                    title={category}
                    description={getCategoryDescription(category)}
                    plans={categoryPlans}
                    onPlanSelect={handlePlanSelect}
                    icon={getCategoryIcon(category)}
                    enrolledPlans={enrolledPlans}
                  />
                ))}

                {/* Completed Plans Section */}
                {completedPlans?.length > 0 && (
                  <section>
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-xl font-heading font-semibold text-foreground">
                        Completed Plans
                      </h2>
                      <span className="text-sm text-muted-foreground">
                        {completedPlans?.length} plan{completedPlans?.length !== 1 ? 's' : ''} completed
                      </span>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                      {completedPlans?.map((plan) => (
                        <div
                          key={plan?.id}
                          className="bg-card border border-border rounded-xl p-6 hover:shadow-warm transition-all duration-200"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <h3 className="font-heading font-semibold text-foreground">
                              {plan?.reading_plans?.name}
                            </h3>
                            <div className="flex items-center space-x-1 text-success">
                              <Icon name="CheckCircle" size={20} />
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground mb-3">
                            {plan?.reading_plans?.description}
                          </p>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Completed:</span>
                            <span className="font-medium text-foreground">
                              {new Date(plan?.completed_at)?.toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {/* No plans available */}
                {availablePlans?.length === 0 && !loading && (
                  <div className="text-center py-12">
                    <Icon name="BookOpen" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                    <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                      No Reading Plans Available
                    </h3>
                    <p className="text-muted-foreground">
                      Check back later for new reading plans.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-6 pb-20 md:pb-6">
        <FadeSlideIn>
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
              <Icon name="Calendar" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <h1 className="text-2xl lg:text-3xl font-heading font-bold text-foreground">
                Reading Plans
              </h1>
              <p className="text-muted-foreground">
                Structured programs to guide your reading journey
              </p>
            </div>
          </div>

          {/* Notification Reminder Banner */}
          <div className="mb-4 flex items-center justify-between bg-primary/5 border border-primary/20 rounded-xl px-4 py-3">
            <div className="flex items-center space-x-3">
              <Icon name="Bell" size={18} color="var(--color-primary)" />
              <p className="text-sm text-foreground">
                <span className="font-medium">Stay on track!</span>{' '}
                <span className="text-muted-foreground">Set up daily reading reminders to never miss a day.</span>
              </p>
            </div>
            <Link
              to="/user-profile"
              state={{ activeTab: 'notifications' }}
              className="flex-shrink-0 text-xs font-semibold text-primary hover:text-primary/80 underline underline-offset-2 transition-colors ml-3"
            >
              Notification Settings →
            </Link>
          </div>

          {/* Main Tab Row */}
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={() => { setMainTab('bible'); setActiveView('plans'); }}
              className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 border ${
                mainTab === 'bible' ?'bg-primary text-primary-foreground border-primary shadow-md' :'bg-card text-muted-foreground border-border hover:text-foreground hover:border-primary/40'
              }`}
            >
              📖 Bible Plans
            </button>
            <button
              onClick={() => { setMainTab('egw'); setEgwView('plans'); }}
              className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 border ${
                mainTab === 'egw' ?'bg-primary text-primary-foreground border-primary shadow-md' :'bg-card text-muted-foreground border-border hover:text-foreground hover:border-primary/40'
              }`}
            >
              ✍️ EGW Plans
            </button>
          </div>
        </div>

        {/* EGW Tab Content */}
        {mainTab === 'egw' && (
          <>
            {egwView === 'reading' && activeEgwEnrollment ? (
              <EGWReadingInterface
                enrollment={activeEgwEnrollment}
                plan={activeEgwPlan}
                onDayComplete={handleEgwDayComplete}
                onBack={() => setEgwView('plans')}
              />
            ) : (
              <div className="space-y-6">
                {egwLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <Icon name="Loader" size={32} color="var(--color-primary)" className="animate-spin mx-auto mb-2" />
                      <p className="text-muted-foreground">Loading EGW plans...</p>
                    </div>
                  </div>
                ) : egwPlans?.length === 0 ? (
                  <div className="text-center py-12">
                    <Icon name="BookOpen" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                    <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                      No EGW Plans Available
                    </h3>
                    <p className="text-muted-foreground">
                      EGW reading plans will appear here once published by an administrator.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Enrolled Plans */}
                    {egwEnrollments?.length > 0 && (
                      <section>
                        <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                          My EGW Plans
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {egwEnrollments?.map(enrollment => {
                            const plan = egwPlans?.find(p => p?.id === enrollment?.plan_id) || enrollment?.egw_reading_plans;
                            if (!plan) return null;
                            return (
                              <EGWPlanCard
                                key={enrollment?.id}
                                plan={plan}
                                enrollment={enrollment}
                                onEnroll={handleEgwEnroll}
                                onContinue={handleEgwContinue}
                                enrolling={egwEnrolling}
                              />
                            );
                          })}
                        </div>
                      </section>
                    )}

                    {/* Available Plans */}
                    <section>
                      <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
                        Available EGW Plans
                      </h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {egwPlans?.filter(plan => !egwEnrollments?.some(e => e?.plan_id === plan?.id))?.map(plan => (
                            <EGWPlanCard
                              key={plan?.id}
                              plan={plan}
                              enrollment={null}
                              onEnroll={handleEgwEnroll}
                              onContinue={handleEgwContinue}
                              enrolling={egwEnrolling}
                            />
                          ))}
                      </div>
                    </section>
                  </>
                )}
              </div>
            )}
          </>
        )}

        {/* Bible Tab Content */}
        {mainTab === 'bible' && (
          <>
            {/* View Navigation */}
            {activeView !== 'plans' && (
              <div className="flex items-center space-x-2 mb-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setActiveView('plans')}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Icon name="ChevronLeft" size={16} />
                  Back to Plans
                </Button>
                <span className="text-muted-foreground">/</span>
                <span className="text-sm font-medium text-foreground">
                  {activeView === 'reading' ? 'Today\'s Reading' : 'Progress Tracker'}
                </span>
              </div>
            )}

            {/* Main Content */}
            {renderMainView()}
          </>
        )}

        {/* Plan Configuration Modal */}
        {isConfigModalOpen && selectedPlan && (
          <PlanConfigModal
            plan={selectedPlan}
            onEnroll={handlePlanEnroll}
            onClose={() => {
              setIsConfigModalOpen(false);
              setSelectedPlan(null);
            }}
            enrolling={enrolling}
          />
        )}
        </FadeSlideIn>
      </main>
    </div>
  );
};

export default BibleReadingPlans;